import { useMemo } from "react";
import { useRootStore } from "../../../../../stores/hooks";
import * as R from "remeda";
import {
  convertLoopingSourceOptionsToDynamicSections,
  memoizedConvertOutputToDynamicSectionOption,
} from "../utils";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import type { Fields } from "@smartsuite/smartsuite-client";
import type {
  IntegrationDescription,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import type { DynamicSection } from "../../../../../types/line";
import type { LoopScope } from "../../../../../stores/automation/looping.store";
import type { LoopingOptionGroup } from "../MemberEditor/utils";
import { loopingToOptions } from "../MemberEditor/utils";
import { resolveEditorLoopScope } from "../utils/action-sources";
import { t } from "i18next";

interface UseLoopingMenuProps {
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  integrations: IntegrationDescription[];
  types: ValueDescription_T[];
  ssFieldTypes: Fields[];
  systemAllowedOptions?: string[];
  action?: ActionCandidate;
  /** Explicit scope, used verbatim when `actionsArePreScoped` is set. A
   * Conditional Logic branch condition passes its own block's enclosing loop:
   * deriving the scope from the left side's resolved action pointed at the
   * loop's SOURCE action, which sits outside the loop, so Current Item never
   * resolved (BUG-21770). `undefined` is meaningful -- a branch outside any
   * loop has no Current Item. */
  loopScope?: LoopScope;
  actionsArePreScoped?: boolean;
}

export const useLoopingMenu = ({
  trigger,
  actions,
  integrations,
  types,
  ssFieldTypes,
  systemAllowedOptions,
  action,
  loopScope,
  actionsArePreScoped,
}: UseLoopingMenuProps): {
  loopingMenu: DynamicSection | undefined;
  loopingFields: LoopingOptionGroup | undefined;
  loopingAction: ActionCandidate | undefined;
} => {
  const store = useRootStore();
  const rootLooping = store.automations.candidate.looping;
  // Current Item is single-scope (nearest enclosing loop): an inner-loop
  // action cannot reference the outer loop's current item (product
  // decision, loop-in-loop v1).
  const looping = resolveEditorLoopScope(
    action,
    rootLooping,
    loopScope,
    actionsArePreScoped,
  );

  const loopingMenu = useMemo(() => {
    if (looping !== undefined) {
      const outputOptions = memoizedConvertOutputToDynamicSectionOption(
        looping,
        actions,
        trigger,
      );

      return (
        outputOptions ??
        convertLoopingSourceOptionsToDynamicSections(
          looping,
          actions,
          integrations,
          types,
          ssFieldTypes,
          systemAllowedOptions,
        )
      );
    }
  }, [looping, actions, integrations, types, ssFieldTypes, action?.hash]);

  const loopingFields = useMemo(() => {
    if (looping !== undefined) {
      const outputOptions = memoizedConvertOutputToDynamicSectionOption(
        looping,
        actions,
        trigger,
      );

      return outputOptions?.options.length
        ? {
            name: t("general.current-item"),
            options: outputOptions.options.map((option) => ({
              title: option.label,
              outputId: option.outputId,
              icon: option.icon,
              contextSpecific: option.contextSpecific,
            })),
            hash: outputOptions?.hash,
          }
        : loopingToOptions(
            looping,
            actions,
            types,
            ssFieldTypes,
            systemAllowedOptions,
          );
    }
  }, [
    looping,
    actions,
    types,
    ssFieldTypes,
    systemAllowedOptions,
    action?.hash,
  ]);

  const loopingAction = useMemo(() => {
    return R.find(actions, (a) => a.hash === loopingMenu?.hash);
  }, [loopingMenu?.hash]);

  return { loopingMenu, loopingFields, loopingAction };
};
